import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useEffect, useMemo, useRef, useState } from 'react';
import BearySprite, { BEARY_ANIMS } from './BearySprite';
import useMediaQuery from '../utils/useMediaQuery';
import useDialog from '../utils/useDialog';
import { logClientError } from '../utils/errorLogger';
import { getKidFriendlyError } from '../utils/kidFriendlyErrors';
import { isSoundEnabled } from '../utils/soundPrefs';

import { API } from '../config';
import { styles } from './countryAdventure/styles';
import WizardScenery from './countryAdventure/WizardScenery';
import { CARD_META, CARD_COPY } from './countryAdventure/constants';
import {
  resolveMediaUrl, countryTheme, initialDiscoveryMedia,
  countryFlagEmoji, countryFlagUrl, loadCountryGeojson,
  normalizeCountryName, featureMatchesCountry, buildCountryShape,
} from './countryAdventure/helpers';
import { SpeakerIcon, PauseIcon, ChevronIcon, ProgressDots } from './countryAdventure/icons';

function CountryFlagImage({ info, style, fallbackStyle, alt }) {
  const [failed, setFailed] = useState(false);
  const flagUrl = countryFlagUrl(info);
  const fallback = countryFlagEmoji(info?.countryCode || info?.code);

  useEffect(() => {
    setFailed(false);
  }, [flagUrl]);

  if (!flagUrl || failed) {
    return <span style={fallbackStyle || style} aria-hidden="true">{fallback}</span>;
  }

  return (
    <img
      src={flagUrl}
      alt={alt || `${info?.name || 'Country'} flag`}
      style={style}
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
}

function useCountryShape(info) {
  const [state, setState] = useState({ status: 'loading', shape: null });
  const countryCode = info?.countryCode || info?.code || '';
  const countryName = info?.name || '';

  useEffect(() => {
    let alive = true;
    setState({ status: 'loading', shape: null });
    const countryInfo = { countryCode, code: countryCode, name: countryName };

    loadCountryGeojson()
      .then((geojson) => {
        if (!alive) return;
        const feature = geojson?.features?.find((candidate) => featureMatchesCountry(candidate, countryInfo));
        const shape = buildCountryShape(feature);
        setState(shape ? { status: 'ready', shape } : { status: 'missing', shape: null });
      })
      .catch(() => {
        if (alive) setState({ status: 'missing', shape: null });
      });

    return () => {
      alive = false;
    };
  }, [countryCode, countryName]);

  return state;
}

function CountryMapBadge({ info, colors, reduced, isMobile }) {
  const { status, shape } = useCountryShape(info);
  const hasShape = status === 'ready' && shape?.paths?.length;
  const introImage = resolveMediaUrl(info?.imageUrl);

  return (
    <motion.div
      style={{ ...styles.mapBadge, ...(isMobile ? styles.mapBadgeMobile : {}) }}
      initial={reduced ? false : { opacity: 0, scale: 0.88, y: 20 }}
      animate={reduced ? { opacity: 1 } : { opacity: 1, scale: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 120, damping: 15, delay: 0.04 }}
    >
      <span style={styles.passportPaperGlow} aria-hidden="true" />
      <span style={styles.passportStamp} aria-hidden="true">{info.countryCode || info.code || 'GO'}</span>
      <div style={styles.mapBadgeHeader}>
        <CountryFlagImage info={info} style={styles.mapFlag} fallbackStyle={styles.mapFlagFallback} />
        <span style={styles.mapBadgeTitle}>{info.name}</span>
      </div>

      <div style={styles.passportSlots}>
        <div style={{ ...styles.mapFrame, ...(isMobile ? styles.mapFrameMobile : {}) }} aria-label={`${info.name} map outline`}>
          <span style={styles.passportSlotLabel}>Map</span>
          {status === 'loading' && (
            <div style={styles.mapSkeleton}>
              <span style={{ ...styles.mapSkeletonBlob, background: colors[0] }} />
              <span style={{ ...styles.mapSkeletonLine, background: colors[1] }} />
            </div>
          )}
          {hasShape && (
            <svg
              viewBox={shape.viewBox}
              preserveAspectRatio="xMidYMid meet"
              role="img"
              aria-label={`${info.name} outline map`}
              style={styles.mapSvg}
            >
              <defs>
                <linearGradient id={`country-map-${info.countryCode || info.code || 'shape'}`} x1="0%" x2="100%" y1="0%" y2="100%">
                  <stop offset="0%" stopColor={colors[1]} />
                  <stop offset="100%" stopColor={colors[0]} />
                </linearGradient>
              </defs>
              {shape.paths.map((path, index) => (
                <path
                  key={`${info.countryCode || info.code || info.name}-${index}`}
                  d={path}
                  fill={`url(#country-map-${info.countryCode || info.code || 'shape'})`}
                  fillRule="evenodd"
                  stroke="#fff7ed"
                  strokeWidth="1.8"
                  vectorEffect="non-scaling-stroke"
                />
              ))}
            </svg>
          )}
          {!hasShape && status !== 'loading' && (
            <div style={styles.mapFallback}>
              <CountryFlagImage info={info} style={styles.mapFallbackFlag} fallbackStyle={styles.mapFallbackEmoji} />
              <span>{info.name}</span>
            </div>
          )}
          <span style={styles.mapDoodleOne} aria-hidden="true" />
          <span style={styles.mapDoodleTwo} aria-hidden="true" />
        </div>

        <span style={styles.passportDivider} aria-hidden="true" />

        <div style={{ ...styles.storyImageFrame, ...(isMobile ? styles.storyImageFrameMobile : {}) }}>
          <span style={styles.photoCornerTopLeft} aria-hidden="true" />
          <span style={styles.photoCornerTopRight} aria-hidden="true" />
          <span style={styles.photoCornerBottomLeft} aria-hidden="true" />
          <span style={styles.photoCornerBottomRight} aria-hidden="true" />
          {introImage ? (
            <img src={introImage} alt={`${info.name} adventure scene`} style={styles.storyImage} loading="lazy" />
          ) : (
            <div style={styles.storyImageFallback}>
              <CountryFlagImage info={info} style={styles.storyFallbackFlag} fallbackStyle={styles.storyFallbackEmoji} />
              <span>{info.name}</span>
            </div>
          )}
          <div style={styles.firstStopStamp}>
            <span>First stop:</span>
            <strong>{info.name}</strong>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function introCopy(info) {
  const { name, content } = info;
  if (normalizeCountryName(name) === 'india') {
    return 'Land of colors, stories, elephants, and festivals.';
  }
  if (content?.tagline) {
    return content.tagline;
  }
  return `I found stories, food, animals, and amazing places in ${name}. Ready to explore?`;
}

function DiscoveryCard({ type, children, opened, expanded, media, status, error, audioState, isAudioActive, onOpen, onPlayAudio, accentColor }) {
  const isMobile = useMediaQuery('(max-width: 640px)');
  const reduced = useReducedMotion();
  const [imgLoaded, setImgLoaded] = useState(false);
  const meta = CARD_META[type];
  const imageUrl = resolveMediaUrl(media?.imageUrl);
  const audioUrl = resolveMediaUrl(media?.audioUrl);
  const ready = Boolean(imageUrl);
  const isExpanded = expanded !== undefined ? expanded : opened;
  const childrenText = typeof children === 'string' ? children.trim() : '';
  const detailText = typeof media?.text === 'string' ? media.text.trim() : '';
  // Avoid duplicating the subtitle (childrenText) inside the reveal when the
  // backend `media.text` is the same string (e.g. landmark name shown twice).
  const showDetailText = Boolean(detailText) && detailText.toLowerCase() !== childrenText.toLowerCase();
  const doneColor = accentColor || meta.color;
  return (
    <motion.div
      role="button"
      tabIndex={0}
      style={{
        ...styles.discoveryCard,
        background: opened ? meta.bg : '#fffaf0',
        borderColor: opened ? meta.color : '#f3d39a',
      }}
      whileTap={{ scale: 0.97 }}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen();
        }
      }}
      aria-pressed={opened}
    >
      <span style={styles.discoveryIcon}>{meta.icon}</span>
      <div style={styles.discoveryText}>
        <strong>{meta.title}</strong>
        <span>{children || 'Tap to discover'}</span>
        <AnimatePresence initial={false}>
          {isExpanded && (
            <motion.div
              key="reveal"
              style={{ overflow: 'hidden' }}
              initial={reduced ? { opacity: 0 } : { height: 0, opacity: 0 }}
              animate={reduced ? { opacity: 1 } : { height: 'auto', opacity: 1 }}
              exit={reduced ? { opacity: 0 } : { height: 0, opacity: 0 }}
              transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
            >
          <div style={styles.discoveryReveal}>
            {status === 'loading' && <span style={styles.mediaLoading}>Painting picture...</span>}
            {status === 'error' && <span style={styles.mediaError}>{error || getKidFriendlyError('discovery')}</span>}
            {imageUrl && (
              <div style={{ ...styles.discoveryImageFrame, ...(isMobile ? styles.discoveryImageFrameMobile : {}) }}>
                <img
                  src={imageUrl}
                  alt={`${meta.title} discovery`}
                  style={{ ...styles.discoveryImage, opacity: imgLoaded ? 1 : 0, transition: 'opacity 400ms var(--ease-out)' }}
                  loading="lazy"
                  onLoad={() => setImgLoaded(true)}
                />
              </div>
            )}
            {showDetailText && <span style={styles.discoveryDetail}>{detailText}</span>}
            {audioUrl && (
              <button
                type="button"
                style={{
                  ...styles.discoveryAudioBtn,
                  ...(isAudioActive ? styles.discoveryAudioBtnActive : {}),
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  onPlayAudio(type);
                }}
              >
                <span aria-hidden="true">{isAudioActive ? '\u23F8' : '\u25B6'}</span>
                {audioState === 'loading' && 'Getting Beary ready'}
                {audioState === 'blocked' && 'Tap to hear Beary'}
                {audioState === 'missing' && getKidFriendlyError('audio')}
                {audioState !== 'loading' && audioState !== 'blocked' && audioState !== 'missing' && (isAudioActive ? 'Pause Beary' : 'Hear Beary')}
              </button>
            )}
          </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <span style={{ ...styles.rewardDot, background: ready ? doneColor : '#f8d48a' }}>
        {status === 'loading' ? '...' : ready ? '✓' : '+'}
      </span>
    </motion.div>
  );
}

function WelcomeStep({ info, colors, reduced, onStart, audioState, isPlaying, onToggleAudio }) {
  const isMobile = useMediaQuery('(max-width: 640px)');
  const isCompact = useMediaQuery('(max-width: 768px)'); // tablet + mobile → stacked layout
  return (
    <motion.div
      key="welcome"
      style={{ ...styles.step, ...(isMobile ? styles.stepMobile : {}) }}
      initial={reduced ? { opacity: 0 } : { opacity: 0, x: 28 }}
      animate={{ opacity: 1, x: 0 }}
      exit={reduced ? { opacity: 0 } : { opacity: 0, x: -28 }}
      transition={{ duration: 0.26, ease: [0.16, 1, 0.3, 1] }}
    >
      <div style={styles.welcomeDecor} aria-hidden="true">
        <span style={styles.decorLeafTop} />
        <span style={styles.decorFeather} />
        <span style={styles.decorLamp} />
        <span style={styles.decorPlant} />
        <span style={styles.decorMonument} />
        <span style={styles.decorBalloon}>
          <svg viewBox="0 0 76 112" width="76" height="112" aria-hidden="true">
            <defs>
              <linearGradient id="balloon-warm" x1="0" x2="1" y1="0" y2="1">
                <stop offset="0" stopColor="#fff7d6" />
                <stop offset="0.48" stopColor="#fb923c" />
                <stop offset="1" stopColor="#0f766e" />
              </linearGradient>
            </defs>
            <path d="M38 7c18 0 30 13 30 31 0 25-22 42-30 50C30 80 8 63 8 38 8 20 20 7 38 7Z" fill="url(#balloon-warm)" />
            <path d="M22 15c-7 22-1 46 16 73M54 15c7 22 1 46-16 73" fill="none" stroke="#fff7ed" strokeWidth="3" opacity="0.75" />
            <path d="M25 89h26l-5 15H30l-5-15Z" fill="#8b5a2b" />
            <path d="M28 89 18 54M48 89l10-35" stroke="#8b5a2b" strokeWidth="2" />
          </svg>
        </span>
      </div>
      <div style={{ ...styles.heroGrid, ...(isCompact ? styles.heroGridMobile : {}) }}>
        <div style={{ ...styles.welcomeVisualStack, ...(isCompact ? styles.welcomeVisualStackMobile : {}) }}>
          <CountryMapBadge info={info} colors={colors} reduced={reduced} isMobile={isCompact} />
          <motion.div
            style={{ ...styles.bearySlot, ...(isCompact ? styles.bearySlotMobile : {}) }}
            initial={reduced ? false : { opacity: 0, x: -70, y: 18 }}
            animate={reduced ? { opacity: 1 } : { opacity: 1, x: 0, y: 0 }}
            transition={{ type: 'spring', stiffness: 120, damping: 14, delay: 0.12 }}
          >
            <BearySprite
              anim={BEARY_ANIMS.waving}
              width={isCompact ? 204 : 230}
              height={isCompact ? 234 : 264}
              countryName={info.name}
            />
          </motion.div>
        </div>

        <div style={{ ...styles.centerCopy, ...(isMobile ? styles.centerCopyMobile : {}) }}>
          <ProgressDots current={1} color={colors[0]} />
          <div style={styles.titleFlagWrap}>
            <h1 style={{ ...styles.title, ...(isMobile ? styles.titleMobile : {}) }}>
              <span>Welcome to</span>
              <strong style={{ ...styles.titleCountry, color: colors[0] }}>{info.name}!</strong>
            </h1>
            <span style={styles.titleSparkLeft} aria-hidden="true" />
            <span style={styles.titleSparkRight} aria-hidden="true" />
          </div>
          <div style={{ ...styles.speechBubble, ...(isMobile ? styles.speechBubbleMobile : {}) }}>
            <span style={styles.bubbleTail} />
            <span style={styles.bubbleNotchRight} />
            <p>{introCopy(info)}</p>
          </div>
          <motion.button
            type="button"
            onClick={onStart}
            style={{
              ...styles.primaryBtn,
              ...(isMobile ? styles.primaryBtnMobile : {}),
              background: `linear-gradient(135deg, ${colors[0]}, #fb923c 52%, #f97316)`,
            }}
            whileTap={{ scale: 0.97 }}
          >
            <CountryFlagImage info={info} style={styles.ctaFlag} fallbackStyle={styles.ctaFlagFallback} />
            Start Exploring
            <ChevronIcon />
          </motion.button>
          <div style={styles.audioControls}>
            {isMobile ? (
              <button
                type="button"
                onClick={onToggleAudio}
                disabled={audioState === 'missing'}
                style={{
                  ...styles.audioBtn,
                  ...(isPlaying ? styles.audioBtnActive : {}),
                  ...(audioState === 'missing' ? styles.audioBtnDisabled : {}),
                }}
              >
                {isPlaying ? <PauseIcon /> : <SpeakerIcon />}
                {isPlaying ? 'Pause Beary' : audioState === 'blocked' ? 'Tap to hear' : audioState === 'loading' ? 'Getting ready' : audioState === 'missing' ? getKidFriendlyError('audio') : 'Hear Beary'}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={onToggleAudio}
                  disabled={audioState === 'missing'}
                  style={{
                    ...styles.audioBtn,
                    ...(isPlaying ? styles.audioBtnActive : {}),
                    ...(audioState === 'missing' ? styles.audioBtnDisabled : {}),
                  }}
                >
                  <SpeakerIcon />
                  {audioState === 'blocked' && 'Tap to hear'}
                  {audioState === 'loading' && 'Getting ready'}
                  {audioState === 'ready' && (isPlaying ? 'Beary Guide On' : 'Hear Beary')}
                  {audioState === 'missing' && getKidFriendlyError('audio')}
                </button>
                <button
                  type="button"
                  onClick={onToggleAudio}
                  disabled={!isPlaying}
                  style={{
                    ...styles.audioBtn,
                    ...styles.audioBtnSecondary,
                    ...(!isPlaying ? styles.audioBtnDisabled : {}),
                  }}
                >
                  <PauseIcon />
                  Pause
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function CardsStep({
  info,
  stepNumber,
  title,
  cardTypes,
  nextLabel,
  footerReadyText,
  footerEmptyText,
  openedCards,
  discoveryMedia,
  discoveryStatus,
  discoveryErrors,
  discoveryAudioState,
  activeDiscoveryAudio,
  onOpenCard,
  onPlayAudio,
  onNext,
  colors,
  reduced,
}) {
  const isMobile = useMediaQuery('(max-width: 640px)');
  const [expandedCard, setExpandedCard] = useState(null);
  const content = info.content || {};
  const readyCount = cardTypes.filter(cardType => discoveryMedia[cardType]?.imageUrl && discoveryMedia[cardType]?.audioUrl).length;
  const totalCards = cardTypes.length;

  return (
    <motion.div
      key={`cards-${stepNumber}`}
      style={{ ...styles.step, ...(isMobile ? styles.stepMobile : {}) }}
      initial={reduced ? { opacity: 0 } : { opacity: 0, x: 28 }}
      animate={{ opacity: 1, x: 0 }}
      exit={reduced ? { opacity: 0 } : { opacity: 0, x: -28 }}
      transition={{ duration: 0.26, ease: [0.16, 1, 0.3, 1] }}
    >
      <div style={{ ...styles.cardsHeader, ...(isMobile ? styles.cardsHeaderMobile : {}) }}>
        <div>
          <ProgressDots current={stepNumber} color={colors[0]} />
          <h2 style={{ ...styles.sectionTitle, ...(isMobile ? styles.sectionTitleMobile : {}) }}>{title}</h2>
        </div>
        <div style={styles.progressBadge}>{readyCount} / {totalCards} ready</div>
      </div>

      <div style={{ ...styles.discoveryGrid, ...(isMobile ? styles.discoveryGridMobile : {}) }}>
        {cardTypes.map((type) => (
          <DiscoveryCard
            key={type}
            type={type}
            opened={openedCards.has(type)}
            expanded={isMobile ? expandedCard === type : openedCards.has(type)}
            media={discoveryMedia[type]}
            status={discoveryStatus[type]}
            error={discoveryErrors[type]}
            audioState={discoveryAudioState[type]}
            isAudioActive={activeDiscoveryAudio === type}
            accentColor={colors[0]}
            onOpen={() => {
              onOpenCard(type);
              if (isMobile) setExpandedCard(prev => prev === type ? null : type);
            }}
            onPlayAudio={onPlayAudio}
          >
            {CARD_COPY[type]?.(content)}
          </DiscoveryCard>
        ))}
      </div>

      <div style={{ ...styles.stepFooter, ...(isMobile ? styles.stepFooterMobile : {}) }}>
        {!isMobile && (
          <BearySprite
            anim={readyCount > 0 ? BEARY_ANIMS.jumping : BEARY_ANIMS.reading}
            width={86}
            height={98}
            countryName={info.name}
          />
        )}
        <p style={styles.footerHint}>
          {readyCount > 0 ? footerReadyText : footerEmptyText}
        </p>
        <button
          type="button"
          onClick={onNext}
          style={{
            ...styles.secondaryBtn,
            ...(isMobile ? styles.secondaryBtnMobile : {}),
          }}
        >
          {nextLabel}
        </button>
      </div>
    </motion.div>
  );
}

function FunFactStep({ info, fact, status, error, onRetry, onNext, onPlayAudio, audioState, isAudioActive, colors, reduced }) {
  const isMobile = useMediaQuery('(max-width: 640px)');
  const isCompact = useMediaQuery('(max-width: 768px)'); // tablet + mobile → stacked layout
  const imageUrl = resolveMediaUrl(fact?.imageUrl);
  const audioUrl = resolveMediaUrl(fact?.audioUrl);
  const isLoading = status === 'loading' || (!fact && !error);

  return (
    <motion.div
      key="wonder"
      style={{ ...styles.step, ...(isMobile ? styles.stepMobile : {}) }}
      initial={reduced ? { opacity: 0 } : { opacity: 0, x: 28 }}
      animate={{ opacity: 1, x: 0 }}
      exit={reduced ? { opacity: 0 } : { opacity: 0, x: -28 }}
      transition={{ duration: 0.26, ease: [0.16, 1, 0.3, 1] }}
    >
      <div style={{ ...styles.wonderLayout, ...(isCompact ? styles.wonderLayoutMobile : {}) }}>
        <div style={styles.wonderCopy}>
          <ProgressDots current={4} color={colors[0]} />
          <h2 style={{ ...styles.sectionTitle, ...(isMobile ? styles.sectionTitleMobile : {}) }}>Fun Fact</h2>
          <div style={{ ...styles.wonderBubble, ...(isMobile ? styles.wonderBubbleMobile : {}) }}>
            <span style={styles.bubbleTail} />
            <p>
              {isLoading && `Beary is finding one amazing thing about ${info.name}...`}
              {!isLoading && error && error}
              {!isLoading && !error && (fact?.text || info.content?.funFact)}
            </p>
          </div>
          {audioUrl && (
            <button
              type="button"
              style={{
                ...styles.audioBtn,
                ...(isAudioActive ? styles.audioBtnActive : {}),
              }}
              onClick={() => onPlayAudio('fact')}
            >
              <span aria-hidden="true">{isAudioActive ? '\u23F8' : '\u25B6'}</span>
              {audioState === 'loading' && 'Getting Beary ready'}
              {audioState === 'blocked' && 'Tap to hear Beary'}
              {audioState === 'missing' && getKidFriendlyError('audio')}
              {audioState !== 'loading' && audioState !== 'blocked' && audioState !== 'missing' && (isAudioActive ? 'Pause Beary' : 'Hear Beary')}
            </button>
          )}
        </div>
        <div style={{ ...styles.wonderVisual, ...(isCompact ? styles.wonderVisualMobile : {}) }}>
          {imageUrl ? (
            <img src={imageUrl} alt={`${info.name} fun fact`} style={styles.wonderImage} loading="lazy" />
          ) : (
            <div style={styles.wonderLoading}>{isLoading ? '\u2728' : '\uD83C\uDF0D'}</div>
          )}
        </div>
        <div style={{ ...styles.wonderFooter, ...(isCompact ? styles.wonderFooterMobile : {}) }}>
          {!isCompact && (
            <BearySprite
              anim={imageUrl ? BEARY_ANIMS.jumping : BEARY_ANIMS.reading}
              width={108}
              height={122}
              countryName={info.name}
            />
          )}
          <p>{imageUrl ? 'Great fact finding! Your explorer badge is waiting.' : 'One more picture is getting ready.'}</p>
          <button
            type="button"
            onClick={imageUrl ? onNext : onRetry}
            disabled={isLoading}
            style={{
              ...styles.primaryBtn,
              ...styles.finishBtn,
              ...(isCompact ? styles.finishBtnMobile : {}),
              background: imageUrl ? `linear-gradient(135deg, ${colors[0]}, #f59e0b)` : '#e5e7eb',
              color: imageUrl ? '#fff' : '#64748b',
            }}
          >
            {imageUrl ? 'Badge Time' : 'Try Again'}
          </button>
        </div>
      </div>
    </motion.div>
  );
}

function RewardStep({ info, colors, reduced, collected, onCollect, onClose }) {
  const isMobile = useMediaQuery('(max-width: 640px)');
  return (
    <motion.div
      key="reward"
      style={{ ...styles.rewardStep, ...(isMobile ? styles.rewardStepMobile : {}) }}
      initial={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0 }}
      transition={{ type: 'spring', stiffness: 200, damping: 18 }}
    >
      <BearySprite
        anim={BEARY_ANIMS.jumping}
        width={isMobile ? 88 : 104}
        height={isMobile ? 100 : 118}
      />
      <div style={{ ...styles.badge, borderColor: colors[0] }}>
        <span style={styles.flagBadge}>
          <CountryFlagImage info={info} style={styles.flagImage} fallbackStyle={styles.flagFallback} />
        </span>
        <strong>{info.name} Explorer</strong>
      </div>
      <ProgressDots current={5} color={colors[0]} />
      <h2 style={{ ...styles.sectionTitle, ...(isMobile ? styles.sectionTitleMobile : {}) }}>
        {collected ? 'Adventure complete!' : 'Beary Explorer badge'}
      </h2>
      <p style={styles.rewardText}>
        {collected
          ? 'You collected discoveries and unlocked a new country badge with Beary.'
          : `Beary found your ${info.name} Explorer badge. Add it to your collection!`}
      </p>
      <button
        type="button"
        onClick={collected ? onClose : onCollect}
        style={{
          ...styles.primaryBtn,
          ...(isMobile ? styles.primaryBtnMobile : {}),
          background: `linear-gradient(135deg, ${colors[0]}, #f59e0b)`,
        }}
      >
        {collected ? 'Back to Globe' : 'Collect Badge'}
      </button>
    </motion.div>
  );
}

export default function CountryAdventureModal({ info, onClose, onCardTap, onReward }) {
  const isMobile = useMediaQuery('(max-width: 640px)');
  const [step, setStep] = useState(0);
  const [openedCards, setOpenedCards] = useState(() => new Set());
  const [discoveryMedia, setDiscoveryMedia] = useState(() => initialDiscoveryMedia(info));
  const [discoveryStatus, setDiscoveryStatus] = useState({});
  const [discoveryErrors, setDiscoveryErrors] = useState({});
  const [discoveryAudioState, setDiscoveryAudioState] = useState({});
  const [activeDiscoveryAudio, setActiveDiscoveryAudio] = useState(null);
  const [audioState, setAudioState] = useState(info?.audioUrl ? 'loading' : 'missing');
  const [isPlaying, setIsPlaying] = useState(false);
  const [badgeCollected, setBadgeCollected] = useState(false);
  const dialogRef = useDialog({ onClose });
  const audioRef = useRef(null);
  const discoveryAudioRef = useRef(null);
  const attemptedAutoplayRef = useRef(false);
  const reduced = useReducedMotion();
  const colors = useMemo(() => countryTheme(info?.name), [info?.name]);
  const audioUrl = resolveMediaUrl(info?.audioUrl);

  useEffect(() => {
    setAudioState(info?.audioUrl ? 'loading' : 'missing');
    setIsPlaying(false);
    attemptedAutoplayRef.current = false;
    setDiscoveryMedia(initialDiscoveryMedia(info));
    setDiscoveryStatus({});
    setDiscoveryErrors({});
    setDiscoveryAudioState({});
    setActiveDiscoveryAudio(null);
    setOpenedCards(new Set());
    setBadgeCollected(false);
    setStep(0);
    if (discoveryAudioRef.current) {
      discoveryAudioRef.current.pause();
      discoveryAudioRef.current = null;
    }
  }, [info]);

  useEffect(() => {
    const audio = audioRef.current;
    return () => {
      if (audio) {
        audio.pause();
      }
      if (discoveryAudioRef.current) {
        discoveryAudioRef.current.pause();
        discoveryAudioRef.current = null;
      }
    };
  }, [audioUrl]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || attemptedAutoplayRef.current || audioState !== 'ready') return;
    attemptedAutoplayRef.current = true;
    if (!isSoundEnabled()) return; // global mute — skip narration autoplay
    audio.currentTime = 0;
    audio.play()
      .then(() => {
        setIsPlaying(true);
      })
      .catch(() => {
        setAudioState('blocked');
        setIsPlaying(false);
      });
  }, [audioState]);

  if (!info) return null;

  const playAudio = () => {
    if (!isSoundEnabled()) return; // global mute (UI_REVIEW §6)
    const audio = audioRef.current;
    if (!audio || audioState === 'missing') return;

    if (!audio.paused) return;

    audio.play()
      .then(() => {
        setAudioState('ready');
        setIsPlaying(true);
      })
      .catch(() => {
        setAudioState('blocked');
        setIsPlaying(false);
      });
  };

  const toggleAudio = () => {
    const audio = audioRef.current;
    if (!audio || audioState === 'missing') return;

    if (!audio.paused) {
      audio.pause();
      setIsPlaying(false);
      return;
    }

    playAudio();
  };

  const stopDiscoveryAudio = () => {
    if (discoveryAudioRef.current) {
      discoveryAudioRef.current.pause();
      discoveryAudioRef.current = null;
    }
    setActiveDiscoveryAudio(null);
  };

  const playDiscoveryAudio = (type, explicitUrl) => {
    if (!isSoundEnabled()) return; // global mute (UI_REVIEW §6)
    const url = resolveMediaUrl(explicitUrl || discoveryMedia[type]?.audioUrl);
    if (!url) return;

    if (activeDiscoveryAudio === type && discoveryAudioRef.current && !discoveryAudioRef.current.paused) {
      stopDiscoveryAudio();
      return;
    }

    if (audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
      setIsPlaying(false);
    }

    stopDiscoveryAudio();
    setDiscoveryAudioState((current) => ({ ...current, [type]: 'loading' }));
    const audio = new Audio(url);
    discoveryAudioRef.current = audio;
    audio.onplay = () => {
      setActiveDiscoveryAudio(type);
      setDiscoveryAudioState((current) => ({ ...current, [type]: 'ready' }));
    };
    audio.onpause = () => {
      setActiveDiscoveryAudio((current) => current === type ? null : current);
    };
    audio.onended = () => {
      setActiveDiscoveryAudio((current) => current === type ? null : current);
    };
    audio.onerror = () => {
      setDiscoveryAudioState((current) => ({ ...current, [type]: 'missing' }));
      setActiveDiscoveryAudio((current) => current === type ? null : current);
    };
    audio.play().catch(() => {
      setDiscoveryAudioState((current) => ({ ...current, [type]: 'blocked' }));
      setActiveDiscoveryAudio(null);
    });
  };

  const openCard = async (type, options = {}) => {
    const shouldPlayNarration = options.playNarration !== false;
    setOpenedCards((current) => {
      if (current.has(type)) return current;
      const next = new Set(current);
      next.add(type);
      return next;
    });

    if (discoveryStatus[type] === 'loading') return;

    if (discoveryMedia[type]?.imageUrl && discoveryMedia[type]?.audioUrl) {
      onCardTap(type);
      if (shouldPlayNarration) playDiscoveryAudio(type);
      return;
    }

    setDiscoveryStatus((current) => ({ ...current, [type]: 'loading' }));
    setDiscoveryErrors((current) => ({ ...current, [type]: null }));

    try {
      const response = await fetch(`${API}/api/country/${info.countryCode}/discovery/${type}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: info.name }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'Discovery media is not ready yet');
      const discovery = payload.data?.discovery;
      if (!discovery?.imageUrl || !discovery?.audioUrl) throw new Error('Discovery is not ready yet');

      setDiscoveryMedia((current) => ({ ...current, [type]: discovery }));
      setDiscoveryStatus((current) => ({ ...current, [type]: 'ready' }));
      onCardTap(type);
      if (shouldPlayNarration) playDiscoveryAudio(type, discovery.audioUrl);
    } catch (err) {
      logClientError(err, {
        type: 'country-discovery',
        details: {
          countryCode: info.countryCode,
          countryName: info.name,
          discoveryType: type,
        },
      });
      setDiscoveryStatus((current) => ({ ...current, [type]: 'error' }));
      setDiscoveryErrors((current) => ({ ...current, [type]: getKidFriendlyError('discovery') }));
    }
  };

  const enterFunFact = () => {
    setStep(3);
    if (!discoveryMedia.fact?.imageUrl || !discoveryMedia.fact?.audioUrl) {
      openCard('fact', { playNarration: false });
    }
  };

  const collectBadge = () => {
    onReward('quiz');
    onReward('country');
    setBadgeCollected(true);
  };

  return (
    <motion.div
      ref={dialogRef}
      tabIndex={-1}
      style={{ ...styles.backdrop, ...(isMobile ? styles.backdropMobile : {}) }}
      role="dialog"
      aria-modal="true"
      aria-label={`Adventure in ${info.name}`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
    >
      <motion.div
        className="orboot-scroll"
        style={{
          ...styles.stage,
          ...(isMobile ? styles.stageMobile : {}),
        }}
        variants={{
          initial: reduced ? { opacity: 0 } : { opacity: 0, scale: 0.97, y: 24 },
          animate: {
            opacity: 1, scale: 1, y: 0,
            transition: { type: 'spring', stiffness: 140, damping: 18 },
          },
          exit: reduced
            ? { opacity: 0, transition: { duration: 0.15 } }
            : { opacity: 0, scale: 0.98, y: 12, transition: { duration: 0.18, ease: [0.4, 0, 1, 1] } },
        }}
        initial="initial"
        animate="animate"
        exit="exit"
      >
        <WizardScenery reduced={reduced} />
        <button type="button" onClick={onClose} style={styles.closeBtn} aria-label="Close adventure">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
            <path d="M6 6l12 12M18 6l-12 12" />
          </svg>
        </button>

        <AnimatePresence mode="wait">
          {step === 0 && (
            <WelcomeStep
              info={info}
              colors={colors}
              reduced={reduced}
              onStart={() => {
                playAudio();
                setStep(1);
              }}
              audioState={audioState}
              isPlaying={isPlaying}
              onToggleAudio={toggleAudio}
            />
          )}
          {step === 1 && (
            <CardsStep
              info={info}
              stepNumber={2}
              title="Animals and Local Food"
              cardTypes={['animal', 'food']}
              nextLabel="Next Discoveries"
              footerReadyText="Great collecting! Beary found more discoveries."
              footerEmptyText="Tap a picture card to hear Beary tell you about it."
              openedCards={openedCards}
              discoveryMedia={discoveryMedia}
              discoveryStatus={discoveryStatus}
              discoveryErrors={discoveryErrors}
              discoveryAudioState={discoveryAudioState}
              activeDiscoveryAudio={activeDiscoveryAudio}
              onOpenCard={openCard}
              onPlayAudio={playDiscoveryAudio}
              onNext={() => setStep(2)}
              colors={colors}
              reduced={reduced}
            />
          )}
          {step === 2 && (
            <CardsStep
              info={info}
              stepNumber={3}
              title="Famous Place and Culture"
              cardTypes={info.content?.culture ? ['landmark', 'culture'] : ['landmark']}
              nextLabel="Fun Fact"
              footerReadyText="Nice exploring! Beary has a fun fact next."
              footerEmptyText="Tap a card to uncover the next discovery."
              openedCards={openedCards}
              discoveryMedia={discoveryMedia}
              discoveryStatus={discoveryStatus}
              discoveryErrors={discoveryErrors}
              discoveryAudioState={discoveryAudioState}
              activeDiscoveryAudio={activeDiscoveryAudio}
              onOpenCard={openCard}
              onPlayAudio={playDiscoveryAudio}
              onNext={enterFunFact}
              colors={colors}
              reduced={reduced}
            />
          )}
          {step === 3 && (
            <FunFactStep
              info={info}
              fact={discoveryMedia.fact}
              status={discoveryStatus.fact}
              error={discoveryErrors.fact}
              onRetry={() => openCard('fact', { playNarration: false })}
              onNext={() => setStep(4)}
              onPlayAudio={playDiscoveryAudio}
              audioState={discoveryAudioState.fact}
              isAudioActive={activeDiscoveryAudio === 'fact'}
              colors={colors}
              reduced={reduced}
            />
          )}
          {step === 4 && (
            <RewardStep
              info={info}
              colors={colors}
              reduced={reduced}
              collected={badgeCollected}
              onCollect={collectBadge}
              onClose={onClose}
            />
          )}
        </AnimatePresence>

        {audioUrl && (
          <audio
            ref={audioRef}
            src={audioUrl}
            preload="auto"
            onCanPlay={() => setAudioState((current) => current === 'blocked' ? current : 'ready')}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onEnded={() => setIsPlaying(false)}
            onError={() => setAudioState('missing')}
          />
        )}
      </motion.div>
    </motion.div>
  );
}
